import { useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Search, AlertTriangle, ArrowDownToLine, ArrowUpFromLine, ClipboardCheck, FileBarChart } from "lucide-react";
import { useApi } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHead, Input, Btn, DataList } from "../components/kit";
import { ItemDialog, MoveDialog } from "../components/stock/StockDialogs";
import { CATEGORY_LABEL, num } from "../lib/constants";

function ItemCard({ i, canEdit, onMove }) {
  const low = i.stock <= i.min_stock;
  const pct = Math.min(100, i.min_stock ? (i.stock / (i.min_stock * 2)) * 100 : 100);
  return (
    <div className={`p-4 rounded-2xl border-2 bg-white shadow-sm ${low ? "border-rose-300" : "border-slate-200"}`} data-testid={`stock-item-${i.id}`}>
      <Link to={`/stok/${i.id}`} className="block">
        <div className="flex justify-between gap-2">
          <div className="min-w-0"><div className="font-bold truncate">{i.name}</div><div className="text-xs text-slate-500">{CATEGORY_LABEL[i.category]}</div></div>
          {low && <span className="shrink-0 h-6 inline-flex items-center gap-1 px-2 rounded bg-rose-600 text-white text-[11px] font-black"><AlertTriangle className="w-3 h-3" /> MENIPIS</span>}
        </div>
        <div className="mt-3 flex items-end gap-1"><span className={`text-3xl font-extrabold font-mono tabular-nums ${low ? "text-rose-600" : ""}`} data-testid={`stock-qty-${i.id}`}>{num(i.stock)}</span><span className="text-sm text-slate-500 mb-1">{i.unit} · min {num(i.min_stock)}</span></div>
        <div className="mt-2 h-1.5 rounded-full bg-slate-100"><div className={`h-full rounded-full ${low ? "bg-rose-500" : "bg-sky-500"}`} style={{ width: `${pct}%` }} /></div>
      </Link>
      {canEdit && (
        <div className="mt-3 grid grid-cols-3 gap-2">
          <button onClick={() => onMove("in", i)} className="h-12 rounded-xl bg-emerald-50 text-emerald-700 font-bold text-sm flex flex-col items-center justify-center" data-testid={`stock-in-btn-${i.id}`}><ArrowDownToLine className="w-4 h-4" />Masuk</button>
          <button onClick={() => onMove("out", i)} className="h-12 rounded-xl bg-rose-50 text-rose-700 font-bold text-sm flex flex-col items-center justify-center" data-testid={`stock-out-btn-${i.id}`}><ArrowUpFromLine className="w-4 h-4" />Keluar</button>
          <button onClick={() => onMove("opname", i)} className="h-12 rounded-xl bg-slate-100 text-slate-700 font-bold text-sm flex flex-col items-center justify-center" data-testid={`stock-opname-btn-${i.id}`}><ClipboardCheck className="w-4 h-4" />Opname</button>
        </div>
      )}
    </div>
  );
}

export default function Stock() {
  const { user } = useAuth();
  const canEdit = ["admin", "gudang"].includes(user.role);
  const [cat, setCat] = useState("");
  const [q, setQ] = useState("");
  const [lowOnly, setLowOnly] = useState(false);
  const [add, setAdd] = useState(false);
  const [move, setMove] = useState({ mode: null, item: null });
  const { data, mutate } = useApi(`/items?${new URLSearchParams({ category: cat, q, low: lowOnly })}`);
  const { data: lows } = useApi("/items?low=true");
  const chip = (v) => `shrink-0 h-11 px-4 rounded-full font-bold text-sm border ${cat === v ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300"}`;
  return (
    <>
      <PageHead title="Stok Barang" sub="Cup polos, tinta, bahan pendukung, kemasan" action={
        <div className="flex gap-2">
          {["admin", "gudang"].includes(user.role) && <Link to="/stok/laporan"><Btn variant="secondary" data-testid="stock-report-btn"><FileBarChart className="w-5 h-5" /> <span className="hidden sm:inline">Laporan</span></Btn></Link>}
          {canEdit && <Btn onClick={() => setAdd(true)} data-testid="add-item-btn"><Plus className="w-5 h-5" /> <span className="hidden sm:inline">Barang</span></Btn>}
        </div>} />
      {lows?.length > 0 && (
        <button onClick={() => setLowOnly(!lowOnly)} className={`w-full text-left p-4 rounded-2xl border-2 flex items-center gap-3 ${lowOnly ? "bg-rose-600 text-white border-rose-600" : "bg-rose-50 border-rose-300 text-rose-800"}`} data-testid="low-stock-banner">
          <AlertTriangle className="w-6 h-6 shrink-0" /><span className="font-bold">{lows.length} barang di bawah stok minimum</span><span className="ml-auto text-sm underline">{lowOnly ? "Tampilkan semua" : "Lihat"}</span>
        </button>
      )}
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1" data-testid="category-chips">
        <button className={chip("")} onClick={() => setCat("")} data-testid="cat-all">Semua</button>
        {Object.entries(CATEGORY_LABEL).map(([k, l]) => <button key={k} className={chip(k)} onClick={() => setCat(k)} data-testid={`cat-${k}`}>{l}</button>)}
      </div>
      <div className="relative"><Search className="w-5 h-5 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" /><Input className="pl-11" placeholder="Cari barang" value={q} onChange={(e) => setQ(e.target.value)} data-testid="stock-search-input" /></div>
      <DataList data={data} emptyText="Belum ada barang." emptyTestId="stock-empty">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="stock-list">
          {(data || []).map((i) => <ItemCard key={i.id} i={i} canEdit={canEdit} onMove={(mode, item) => setMove({ mode, item })} />)}
        </div>
      </DataList>
      <ItemDialog open={add} onClose={() => setAdd(false)} onSaved={() => { setAdd(false); mutate(); }} />
      <MoveDialog mode={move.mode} item={move.item} onClose={() => setMove({ mode: null, item: null })} onSaved={() => { setMove({ mode: null, item: null }); mutate(); }} />
    </>
  );
}
